import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Table,
  Select,
  Statistic,
  Progress,
  Spin,
  Button,
  Space,
} from "antd";
import { useNavigate, Link } from "react-router-dom";
import {
  UserOutlined,
  TeamOutlined,
  BankOutlined,
  DollarOutlined,
  RiseOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  StopOutlined,
  HeartOutlined,
  BookOutlined,
} from "@ant-design/icons";
import { API_ENDPOINTS } from "../config/api";
import { useGet } from "../hooks/useApi";
import Toast from "../utils/toast";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

const { Option } = Select;

const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // State for filters
  const [userTypeFilter, setUserTypeFilter] = useState("all");
  const [timePeriod, setTimePeriod] = useState("lifetime");

  // State for dashboard data
  const [dashboardData, setDashboardData] = useState({
    user_statistics: {
      total_users: 0,
      system_admins: 0,
      by_type: [],
    },
    revenue_statistics: {
      total_revenue: 0,
      currency: "EUR",
      by_user_type: [],
      by_package_type: [],
      monthly_trend: [],
    },
    recent_transactions: [],
    new_paid_users: [],
    upcoming_expirations: [],
  });

  // State for most liked exercises
  const [mostLikedExercises, setMostLikedExercises] = useState([]);
  const [mostLikedLoading, setMostLikedLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  // ========== Training Component States ==========
  const [trainings, setTrainings] = useState([]);
  const [trainingsLoading, setTrainingsLoading] = useState(false);
  const [trainingTotalCount, setTrainingTotalCount] = useState(0);
  const [trainingFilter, setTrainingFilter] = useState("current_month");
  // Separate month and year
  const [selectedMonth, setSelectedMonth] = useState(dayjs().month() + 1); // 1-12
  const [selectedYear, setSelectedYear] = useState(dayjs().year());

  // Build API URL for training (dynamic, with date filters)
  const buildTrainingApiUrl = () => {
    let start_date = null;
    let end_date = null;

    if (trainingFilter === "current_month") {
      const now = dayjs();
      start_date = now.startOf("month").format("YYYY-MM-DD");
      end_date = now.endOf("month").format("YYYY-MM-DD");
    } else if (
      trainingFilter === "selected_month" &&
      selectedMonth &&
      selectedYear
    ) {
      const date = dayjs(`${selectedYear}-${selectedMonth}-01`);
      start_date = date.startOf("month").format("YYYY-MM-DD");
      end_date = date.endOf("month").format("YYYY-MM-DD");
    }

    const params = new URLSearchParams();
    if (start_date) params.append("start_date", start_date);
    if (end_date) params.append("end_date", end_date);

    let url = `${API_ENDPOINTS.TRAINING_COMPONENT_DASHBOARD}?${params.toString()}`;
    return url;
  };

  // Build API URL for dashboard data (existing)
  const buildApiUrl = () => {
    let url = `${API_ENDPOINTS.DASHBOARD_DATA}?`;
    if (userTypeFilter && userTypeFilter !== "all") {
      url += `user_type=${userTypeFilter}&`;
    }
    if (timePeriod) {
      url += `time_period=${timePeriod}`;
    }
    return url;
  };

  // API hooks
  const { execute: fetchDashboardData, loading: apiLoading } = useGet(
    buildApiUrl(),
    false,
  );
  const { execute: fetchMostLikedExercises } = useGet(
    API_ENDPOINTS.EXERCISE_MOST_LIKED,
    false,
  );

  // Training API hook
  const { execute: fetchTrainingsApi, loading: trainingsApiLoading } = useGet(
    buildTrainingApiUrl(),
    false,
  );

  // Load dashboard data
  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const response = await fetchDashboardData();
      if (response?.status === true && response.data) {
        setDashboardData(response.data);
      } else {
        Toast.error(response?.message || t("dashboard.failedLoadDashboard"));
      }
    } catch (error) {
      console.error("Error loading dashboard data:", error);
      Toast.error(error.message || t("dashboard.failedLoadDashboard"));
    } finally {
      setLoading(false);
    }
  };

  // Load most liked exercises
  const loadMostLikedExercises = async () => {
    setMostLikedLoading(true);
    try {
      const response = await fetchMostLikedExercises();
      if (response?.status === true && response.data) {
        setMostLikedExercises(response.data);
      } else {
        console.warn("Failed to load most liked exercises", response?.message);
      }
    } catch (error) {
      console.error("Error loading most liked exercises:", error);
    } finally {
      setMostLikedLoading(false);
    }
  };

  // Load training components
  const loadTrainings = async () => {
    setTrainingsLoading(true);
    try {
      const response = await fetchTrainingsApi();
      if (response?.status === true && response.data) {
        setTrainings(response.data);
        setTrainingTotalCount(
          response.pagination?.total_count || response.data.length,
        );
      } else {
        setTrainings([]);
        setTrainingTotalCount(0);
      }
    } catch (error) {
      console.error("Error loading trainings:", error);
      Toast.error(t("dashboard.failedToLoadTrainings"));
    } finally {
      setTrainingsLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    loadDashboardData();
    loadMostLikedExercises();
  }, []);

  // Fetch data when filters change
  useEffect(() => {
    if (userTypeFilter || timePeriod) {
      loadDashboardData();
    }
  }, [userTypeFilter, timePeriod]);

  // Fetch trainings when filter or month/year changes
  useEffect(() => {
    loadTrainings();
  }, [trainingFilter, selectedMonth, selectedYear]);

  // Formatting functions (unchanged)
  const formatCurrency = (amount, currency = "EUR") => {
    if (!amount && amount !== 0) return "N/A";
    const symbols = { EUR: "€", USD: "$", INR: "₹" };
    return `${symbols[currency] || "€"}${parseFloat(amount).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === "nl" ? "nl-NL" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === "nl" ? "nl-NL" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getPaymentStatusBadge = (status) => {
    const styles = {
      SUCCESS: {
        color: "#52c41a",
        background: "#f6ffed",
        border: "1px solid #b7eb8f",
      },
      FAILED: {
        color: "#f5222d",
        background: "#fff1f0",
        border: "1px solid #ffa39e",
      },
      REFUNDED: {
        color: "#faad14",
        background: "#fff7e6",
        border: "1px solid #ffd591",
      },
    };
    const style = styles[status] || styles.SUCCESS;
    return (
      <span
        style={{
          padding: "2px 8px",
          borderRadius: "4px",
          fontSize: "12px",
          fontWeight: "500",
          ...style,
        }}
      >
        {status
          ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
          : "Unknown"}
      </span>
    );
  };

  const getUserTypeLabel = (type) => {
    const labels = {
      INDIVIDUAL: t("userManager.individual"),
      CLUB_COACH: t("userManager.clubCoach"),
      CLUB_ADMIN: t("userManager.clubAdmin"),
      KNKV_PARTICIPANT: t("userManager.knkvParticipant"),
      KNKV_INSTRUCTOR: t("userManager.knkvInstructor"),
      SYSTEM_ADMIN: t("userManager.systemAdmin"),
    };
    return labels[type] || type;
  };

  const getFilteredRevenue = () =>
    dashboardData.revenue_statistics.total_revenue || 0;

  const handleUserTypeFilter = (value) => setUserTypeFilter(value);
  const handleTimePeriodChange = (value) => setTimePeriod(value);

  const handleUserTypeClick = (userType) =>
    navigate(`/users?user_type=${userType}`);
  const handleRevenueClick = () =>
    navigate(
      `/subscriptiontransaction?period=${timePeriod}&user_type=${userTypeFilter}`,
    );
  const handleTransactionClick = (transactionId) =>
    navigate(`/subscription-transactions/details/${transactionId}`);
  const handleNewUserClick = (record) =>
    navigate(`/users/details/${record.id}`);
  const handleExpirationClick = (userName) =>
    navigate(`/users?name=${encodeURIComponent(userName)}&expiring=true`);

  // Table columns (existing)
  const transactionColumns = [
    {
      title: t("dashboard.transactionId"),
      dataIndex: "transaction_id",
      key: "transaction_id",
      width: 140,
      render: (text) => (
        <Link
          onClick={() => handleTransactionClick(text)}
          style={{ color: "#1890ff", cursor: "pointer" }}
        >
          {text || "N/A"}
        </Link>
      ),
    },
    {
      title: t("dashboard.userName"),
      dataIndex: "user_name",
      key: "user_name",
      render: (text) => (
        <Link
          onClick={() => handleNewUserClick(text)}
          style={{ color: "#1890ff", cursor: "pointer" }}
        >
          {text || "N/A"}
        </Link>
      ),
    },
    {
      title: t("dashboard.userType"),
      dataIndex: "user_type_label",
      key: "user_type",
      render: (text) => text || "N/A",
    },
    {
      title: t("dashboard.amount"),
      key: "amount",
      render: (_, record) => formatCurrency(record.amount, record.currency),
    },
    {
      title: t("dashboard.plan"),
      dataIndex: "subscription_plan",
      key: "subscription_plan",
      render: (text) => text || "N/A",
    },
    {
      title: t("dashboard.dateTime"),
      dataIndex: "transaction_date",
      key: "transaction_date",
      render: (date) => formatDate(date),
    },
    {
      title: t("common.status"),
      dataIndex: "payment_status",
      key: "payment_status",
      render: (status) => getPaymentStatusBadge(status),
    },
  ];

  const newUsersColumns = [
    {
      title: t("dashboard.userName"),
      dataIndex: "user_name",
      key: "user_name",
      render: (text) => (
        <a
          onClick={() => handleNewUserClick(text)}
          style={{ color: "#1890ff", cursor: "pointer" }}
        >
          {text || "N/A"}
        </a>
      ),
    },
    {
      title: t("dashboard.userType"),
      dataIndex: "user_type_label",
      key: "user_type",
      render: (text) => text || "N/A",
    },
    {
      title: t("dashboard.plan"),
      dataIndex: "subscription_plan",
      key: "subscription_plan",
      render: (text) => text || "N/A",
    },
    {
      title: t("dashboard.amountPaid"),
      key: "amount_paid",
      render: (_, record) =>
        formatCurrency(record.amount_paid, record.currency),
    },
    {
      title: t("dashboard.activationDate"),
      dataIndex: "activation_date",
      key: "activation_date",
      render: (date) => formatDateShort(date),
    },
  ];

  const expirationColumns = [
    {
      title: t("dashboard.userName"),
      dataIndex: "user_name",
      key: "user_name",
      render: (text) => (
        <a
          onClick={() => handleExpirationClick(text)}
          style={{ color: "#1890ff", cursor: "pointer" }}
        >
          {text || "N/A"}
        </a>
      ),
    },
    {
      title: t("dashboard.userType"),
      dataIndex: "user_type_label",
      key: "user_type",
      render: (text) => text || "N/A",
    },
    {
      title: t("dashboard.plan"),
      dataIndex: "subscription_plan",
      key: "subscription_plan",
      render: (text) => text || "N/A",
    },
    {
      title: t("dashboard.expirationDate"),
      dataIndex: "expiration_date",
      key: "expiration_date",
      render: (date) => formatDateShort(date),
    },
    {
      title: t("dashboard.daysRemaining"),
      dataIndex: "days_remaining",
      key: "days_remaining",
      render: (days) =>
        days ? (
          <span
            style={{
              color: days <= 7 ? "#f5222d" : days <= 14 ? "#faad14" : "#52c41a",
              fontWeight: "bold",
            }}
          >
            {days} {t("dashboard.days")}
          </span>
        ) : (
          "N/A"
        ),
    },
  ];

  const mostLikedColumns = [
    {
      title: t("dashboard.exerciseTitle"),
      dataIndex: "title",
      key: "title",
      render: (text, record) => (
        <a
          href={record.video_url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#1890ff" }}
        >
          {text || "N/A"}
        </a>
      ),
    },
    {
      title: t("dashboard.theme"),
      dataIndex: "theme_name",
      key: "theme_name",
      render: (text) => text || "N/A",
    },
    {
      title: t("dashboard.component"),
      dataIndex: "component",
      key: "component",
      render: (text) => text || "N/A",
    },
    {
      title: t("dashboard.ageGroup"),
      dataIndex: "age_group",
      key: "age_group",
      render: (text) => text || "N/A",
    },
    {
      title: t("dashboard.totalLikes"),
      dataIndex: "total_likes",
      key: "total_likes",
      sorter: (a, b) => a.total_likes - b.total_likes,
      render: (likes) => (
        <span style={{ fontWeight: "bold", color: "#eb2f96" }}>
          <HeartOutlined style={{ marginRight: 4 }} />
          {likes}
        </span>
      ),
    },
  ];

  const trainingColumns = [
    {
      title: t("dashboard.trainingName"),
      dataIndex: "name",
      key: "name",
      render: (text, record) => text || record.title || "N/A",
    },
    // {
    //   title: t("dashboard.displayOrder"),
    //   dataIndex: "display_order",
    //   key: "display_order",
    //   sorter: (a, b) => a.display_order - b.display_order,
    // },
    {
      title: t("dashboard.createdAt"),
      dataIndex: "created_at",
      key: "created_at",
      render: (date) => formatDateShort(date),
    },
  ];

  // Generate year options (e.g., 2 years before and after current year)
  const getYearOptions = () => {
    const currentYear = dayjs().year();
    const years = [];
    for (let year = currentYear - 2; year <= currentYear + 2; year++) {
      years.push({ value: year, label: year.toString() });
    }
    return years;
  };

  // Month options (1-12)
  const monthOptions = [
    { value: 1, label: t("common.january") },
    { value: 2, label: t("common.february") },
    { value: 3, label: t("common.march") },
    { value: 4, label: t("common.april") },
    { value: 5, label: t("common.may") },
    { value: 6, label: t("common.june") },
    { value: 7, label: t("common.july") },
    { value: 8, label: t("common.august") },
    { value: 9, label: t("common.september") },
    { value: 10, label: t("common.october") },
    { value: 11, label: t("common.november") },
    { value: 12, label: t("common.december") },
  ];

  const totalUsers = dashboardData.user_statistics.total_users || 0;

  return (
    <div className="dashboard-page">
      {/* Header */}
      <div
        style={{
          marginBottom: "24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <h1 style={{ fontSize: "28px", fontWeight: "600" }}>
            {t("dashboard.title")}
          </h1>
          <p style={{ color: "#666", fontSize: "16px" }}>
            {t("dashboard.subtitle")}
          </p>
        </div>
        {loading && <Spin tip={t("common.loading")} />}
      </div>

      {/* User Statistics Section */}
      <Card
        title={
          <span>
            <TeamOutlined style={{ marginRight: "8px", color: "#1890ff" }} />
            {t("dashboard.userStatistics")}
          </span>
        }
        style={{ marginBottom: "24px", cursor: "pointer" }}
        loading={loading}
        onClick={() => navigate("/users")}
        hoverable
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card bordered={false} style={{ background: "#f0f5ff" }}>
              <Statistic
                title={t("dashboard.totalUsers")}
                value={totalUsers}
                prefix={<UserOutlined />}
                valueStyle={{ color: "#1890ff", fontWeight: "bold" }}
              />
              <div style={{ marginTop: "8px", color: "#666" }}>
                <small>
                  {t("dashboard.includingAdmins", {
                    count: dashboardData.user_statistics.system_admins || 0,
                  })}
                </small>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={16} lg={18}>
            <Row gutter={[16, 16]}>
              {dashboardData.user_statistics.by_type.map((item) => {
                const colors = {
                  INDIVIDUAL: "#1890ff",
                  CLUB_COACH: "#52c41a",
                  CLUB_ADMIN: "#faad14",
                  KNKV_PARTICIPANT: "#722ed1",
                  KNKV_INSTRUCTOR: "#eb2f96",
                };
                const color = colors[item.type] || "#1890ff";
                return (
                  <Col xs={24} sm={12} md={8} key={item.type}>
                    <Card
                      size="small"
                      bordered={false}
                      style={{ background: `${color}10`, cursor: "pointer" }}
                      hoverable
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUserTypeClick(item.type);
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontSize: "14px",
                              color: "#666",
                              marginBottom: "4px",
                            }}
                          >
                            {item.label}
                          </div>
                          <div
                            style={{
                              fontSize: "20px",
                              fontWeight: "bold",
                              color,
                            }}
                          >
                            {item.count}
                          </div>
                        </div>
                        <Progress
                          type="circle"
                          percent={item.percentage}
                          width={50}
                          strokeColor={color}
                          format={(percent) => `${percent}%`}
                        />
                      </div>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </Col>
        </Row>
      </Card>

      {/* Revenue Section */}
      <Card
        title={
          <span>
            <DollarOutlined style={{ marginRight: "8px", color: "#52c41a" }} />
            {t("dashboard.subscriptionRevenue")}
          </span>
        }
        style={{ marginBottom: "24px", cursor: "pointer" }}
        loading={loading}
        onClick={handleRevenueClick}
        hoverable
        extra={
          <div
            style={{ display: "flex", gap: "16px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <Select
              placeholder={t("dashboard.userTypePlaceholder")}
              style={{ width: 180 }}
              value={userTypeFilter}
              onChange={handleUserTypeFilter}
              allowClear
            >
              <Option value="all">{t("dashboard.allUsers")}</Option>
              <Option value="INDIVIDUAL">
                {t("dashboard.individualUsers")}
              </Option>
              <Option value="CLUB_COACH">{t("dashboard.clubCoaches")}</Option>
              <Option value="CLUB_ADMIN">{t("dashboard.clubAdmins")}</Option>
              <Option value="KNKV_PARTICIPANT">
                {t("dashboard.knkvParticipants")}
              </Option>
              <Option value="KNKV_INSTRUCTOR">
                {t("dashboard.knkvInstructors")}
              </Option>
            </Select>
            <Select
              placeholder={t("dashboard.timePeriod")}
              style={{ width: 150 }}
              value={timePeriod}
              onChange={handleTimePeriodChange}
            >
              <Option value="last_week">{t("dashboard.lastWeek")}</Option>
              <Option value="last_month">{t("dashboard.lastMonth")}</Option>
              <Option value="last_3_months">
                {t("dashboard.last3Months")}
              </Option>
              <Option value="last_6_months">
                {t("dashboard.last6Months")}
              </Option>
              <Option value="last_9_months">
                {t("dashboard.last9Months")}
              </Option>
              <Option value="last_12_months">
                {t("dashboard.last12Months")}
              </Option>
              <Option value="lifetime">{t("dashboard.lifetime")}</Option>
            </Select>
          </div>
        }
      >
        <Row gutter={24}>
          <Col xs={24} sm={12} md={8}>
            <Card bordered={false} style={{ background: "#f6ffed" }}>
              <Statistic
                title={t("dashboard.totalRevenue")}
                value={
                  dashboardData.revenue_statistics.total_revenue_all_time || 0
                }
                precision={2}
                prefix={
                  dashboardData.revenue_statistics.currency === "EUR"
                    ? "€"
                    : "$"
                }
                valueStyle={{ color: "#52c41a", fontWeight: "bold" }}
              />
              <div style={{ marginTop: "8px", color: "#666" }}>
                <RiseOutlined
                  style={{ marginRight: "4px", color: "#52c41a" }}
                />
                <span>{t("dashboard.lifetimeRevenue")}</span>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card bordered={false} style={{ background: "#fff7e6" }}>
              <Statistic
                title={`${t("dashboard.totalRevenue")} (${timePeriod.replace(/_/g, " ")})`}
                value={getFilteredRevenue()}
                precision={2}
                prefix={
                  dashboardData.revenue_statistics.currency === "EUR"
                    ? "€"
                    : "$"
                }
                valueStyle={{ color: "#faad14", fontWeight: "bold" }}
              />
              <div style={{ marginTop: "8px", color: "#666" }}>
                <ClockCircleOutlined
                  style={{ marginRight: "4px", color: "#faad14" }}
                />
                <span>{t("dashboard.basedOnSelectedPeriod")}</span>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Card bordered={false} style={{ background: "#f9f0ff" }}>
              <div
                style={{ fontSize: "14px", color: "#666", marginBottom: "8px" }}
              >
                {t("dashboard.revenueByPackageType")}
              </div>
              {dashboardData.revenue_statistics.by_package_type.map(
                (item, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "4px",
                    }}
                  >
                    <span>{item.label}:</span>
                    <span style={{ fontWeight: "bold" }}>
                      {formatCurrency(
                        item.revenue,
                        dashboardData.revenue_statistics.currency,
                      )}{" "}
                      ({item.percentage}%)
                    </span>
                  </div>
                ),
              )}
            </Card>
          </Col>
        </Row>
      </Card>

      {/* Recent Activity Panels */}
      <Row gutter={[24, 24]}>
        <Col xs={24} xl={12}>
          <Card
            title={
              <span>
                <BankOutlined
                  style={{ marginRight: "8px", color: "#722ed1" }}
                />
                {t("dashboard.last10Transactions")}
              </span>
            }
            extra={
              <a href="/subscriptiontransaction">{t("dashboard.viewAll")}</a>
            }
            loading={loading}
          >
            <div className="table-responsive">
              <Table
                dataSource={dashboardData.recent_transactions}
                columns={transactionColumns}
                rowKey={(record) => record.id || Math.random()}
                size="small"
                pagination={false}
                locale={{ emptyText: t("dashboard.noTransactionsFound") }}
                onRow={(record) => ({
                  style: { cursor: "pointer" },
                  onClick: () => handleTransactionClick(record.id),
                })}
              />
            </div>
          </Card>
        </Col>
        <Col xs={24} xl={12}>
          <Card
            title={
              <span>
                <CheckCircleOutlined
                  style={{ marginRight: "8px", color: "#52c41a" }}
                />
                {t("dashboard.last10NewPaidUsers")}
              </span>
            }
            extra={<a href="/new-paid-users">{t("dashboard.viewAll")}</a>}
            loading={loading}
          >
            <div className="table-responsive">
              <Table
                dataSource={dashboardData.new_paid_users}
                columns={newUsersColumns}
                rowKey={(record, index) => index}
                size="small"
                pagination={false}
                locale={{ emptyText: t("dashboard.noNewPaidUsers") }}
                onRow={(record) => ({
                  style: { cursor: "pointer" },
                  onClick: () => handleNewUserClick(record),
                })}
              />
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]} style={{ marginTop: "24px" }}>
        <Col xs={24}>
          <Card
            title={
              <span>
                <StopOutlined
                  style={{ marginRight: "8px", color: "#f5222d" }}
                />
                {t("dashboard.upcomingSubscriptionExpirations")}
              </span>
            }
            extra={<a href="/users?expiring=true">{t("dashboard.viewAll")}</a>}
            loading={loading}
          >
            <Table
              dataSource={dashboardData.upcoming_expirations}
              columns={expirationColumns}
              rowKey={(record, index) => index}
              size="small"
              pagination={false}
              locale={{ emptyText: t("dashboard.noUpcomingExpirations") }}
              onRow={(record) => ({
                style: { cursor: "pointer" },
                onClick: () => handleExpirationClick(record.user_name),
              })}
            />
          </Card>
        </Col>
      </Row>

      {/* Most Favorite Exercises */}
      <Row gutter={[24, 24]} style={{ marginTop: "24px" }}>
        <Col xs={24}>
          <Card
            title={
              <span>
                <HeartOutlined
                  style={{ marginRight: "8px", color: "#eb2f96" }}
                />
                {t("dashboard.mostFavoriteExercises")}
              </span>
            }
            extra={<a href="/exercise">{t("dashboard.viewAll")}</a>}
            loading={mostLikedLoading}
          >
            <Table
              dataSource={mostLikedExercises}
              columns={mostLikedColumns}
              rowKey={(record) => record.id}
              size="small"
              pagination={{ pageSize: 10 }}
              locale={{ emptyText: t("dashboard.noExercisesFound") }}
            />
          </Card>
        </Col>
      </Row>

      {/* ========== TRAINING COMPONENTS SECTION ========== */}
      <Row gutter={[24, 24]} style={{ marginTop: "24px" }}>
        <Col xs={24}>
          <Card
            title={
              <span>
                <BookOutlined
                  style={{ marginRight: "8px", color: "#1890ff" }}
                />
                {t("dashboard.trainingComponents")}
              </span>
            }
            extra={
              <Space>
                <Button
                  type={
                    trainingFilter === "current_month" ? "primary" : "default"
                  }
                  onClick={() => {
                    setTrainingFilter("current_month");
                    setSelectedMonth(dayjs().month() + 1);
                    setSelectedYear(dayjs().year());
                  }}
                >
                  {t("dashboard.currentMonth")}
                </Button>
                <Select
                  placeholder={t("dashboard.selectMonth")}
                  value={
                    trainingFilter === "selected_month"
                      ? selectedMonth
                      : undefined
                  }
                  onChange={(value) => {
                    setSelectedMonth(value);
                    setTrainingFilter("selected_month");
                  }}
                  style={{ width: 120 }}
                  options={monthOptions}
                />
                <Select
                  placeholder={t("dashboard.selectYear")}
                  value={
                    trainingFilter === "selected_month"
                      ? selectedYear
                      : undefined
                  }
                  onChange={(value) => {
                    setSelectedYear(value);
                    setTrainingFilter("selected_month");
                  }}
                  style={{ width: 100 }}
                  options={getYearOptions()}
                />
              </Space>
            }
            loading={trainingsLoading}
          >
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={24}>
                <Statistic
                  title={t("dashboard.totalTrainingComponents")}
                  value={trainingTotalCount}
                  prefix={<BookOutlined />}
                  valueStyle={{ color: "#1890ff" }}
                />
              </Col>
            </Row>
            <Table
              dataSource={trainings}
              columns={trainingColumns}
              rowKey="id"
              pagination={{ pageSize: 10, showSizeChanger: true }}
              locale={{ emptyText: t("dashboard.noTrainingsFound") }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
